// src/contexts/AuthContext.jsx - Fixed loading loop issue
import { supabase } from '@/lib/supabase';
import { createContext, useContext, useEffect, useState } from 'react';

const AuthContext = createContext({
  user: null,
  profile: null,
  session: null,
  loading: true,
  signUp: async () => {},
  signIn: async () => {},
  signOut: async () => {},
  isAdmin: false,
  isOperator: false,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    // Get initial session
    const getSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Error getting session:', error);
          if (isMounted) {
            setLoading(false);
            setIsInitialized(true);
          }
          return;
        }
        
        if (isMounted) {
          setSession(session);
          setUser(session?.user || null);
          
          if (session?.user) {
            await fetchUserProfile(session.user.id);
          } else {
            setLoading(false);
          }
          setIsInitialized(true);
        }
      } catch (error) {
        console.error('Error in getSession:', error);
        if (isMounted) {
          setLoading(false);
          setIsInitialized(true);
        }
      }
    };

    getSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event, session?.user?.email);
        
        if (!isMounted) return;
        
        // CRITICAL FIX: Ignore INITIAL_SESSION events after initialization
        if (event === 'INITIAL_SESSION' && isInitialized) {
          console.log('🚫 Ignoring INITIAL_SESSION - already initialized');
          return;
        }
        
        setSession(session);
        setUser(session?.user || null);
        
        if (event === 'SIGNED_IN' && session?.user) {
          console.log('User signed in, fetching profile for:', session.user.id);
          await fetchUserProfile(session.user.id);
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          console.log('Token refreshed, keeping existing state - no profile refetch needed');
          setLoading(false);
        } else if (event === 'SIGNED_OUT') {
          console.log('User signed out, clearing profile');
          setProfile(null);
          setLoading(false);
        } else if (event === 'INITIAL_SESSION' && session?.user && !isInitialized) {
          console.log('Initial session with user, fetching profile...');
          await fetchUserProfile(session.user.id);
        } else {
          setLoading(false);
        }
      }
    );

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, []); // REMOVED profile from dependencies to prevent loops

  // FIXED: Fetch user profile with timeout to prevent infinite hanging
  const fetchUserProfile = async (userId) => {
    console.log('🔵 START: fetchUserProfile called for user:', userId);
    console.log('🔵 Current loading state:', loading);
    console.log('🔵 Current profile state:', profile);
    
    try {
      console.log('🔵 About to query supabase profiles table...');
      
      // CRITICAL FIX: Add timeout to prevent hanging queries
      const queryPromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Query timeout after 5 seconds')), 5000)
      );
      
      const queryStart = Date.now();
      console.log('🔵 Starting query with 5-second timeout...');
      
      const { data, error } = await Promise.race([queryPromise, timeoutPromise]);
      
      const queryEnd = Date.now();
      console.log(`🔵 Supabase query completed in ${queryEnd - queryStart}ms`);
      console.log('🔵 Query result - data:', data);
      console.log('🔵 Query result - error:', error);
      
      if (error) {
        console.log('🔴 Error detected, error code:', error.code);
        if (error.code === 'PGRST116') {
          console.log('🔴 No profile found (PGRST116), creating default profile...');
          await createDefaultProfile(userId);
          return;
        }
        console.log('🔴 Throwing error:', error);
        throw error;
      }

      if (data) {
        console.log('🟢 Profile data found, setting profile state...');
        console.log('🟢 Profile data:', JSON.stringify(data, null, 2));
        setProfile(data);
        console.log('🟢 Profile state set successfully');
      } else {
        console.log('🟡 No data returned but no error, creating default profile...');
        await createDefaultProfile(userId);
        return;
      }
      
    } catch (error) {
      console.error('🔴 CATCH: Error in fetchUserProfile:', error);
      console.error('🔴 Error message:', error.message);
      
      // If it's a timeout or connection error, create emergency profile immediately
      if (error.message?.includes('timeout') || error.message?.includes('network')) {
        console.log('🔴 Network/timeout error detected, creating emergency profile immediately');
      }
      
      if (!profile) {
        console.log('🔴 No existing profile, creating emergency profile...');
        const emergencyProfile = {
          id: userId,
          email: user?.email || '',
          role: 'registrato',
          first_name: '',
          last_name: ''
        };
        console.log('🔴 Emergency profile:', emergencyProfile);
        setProfile(emergencyProfile);
        console.log('🔴 Emergency profile set');
      } else {
        console.log('🔴 Keeping existing profile:', profile.role);
      }
    } finally {
      console.log('🔵 FINALLY: Setting loading to false in fetchUserProfile');
      setLoading(false);
      console.log('🔵 FINALLY: Loading set to false, current loading state should be false');
    }
    
    console.log('🔵 END: fetchUserProfile completed');
  };

  // FIXED: Create default profile with detailed logging
  const createDefaultProfile = async (userId) => {
    console.log('🟡 START: createDefaultProfile called for user:', userId);
    
    try {
      console.log('🟡 Getting user email from supabase.auth.getUser()...');
      
      const { data: userData } = await supabase.auth.getUser();
      console.log('🟡 User data received:', userData);
      
      const email = userData?.user?.email;
      console.log('🟡 Extracted email:', email);
      
      if (!email) {
        console.error('🔴 No email found for user');
        const fallbackProfile = {
          id: userId,
          email: '',
          role: 'registrato',
          first_name: '',
          last_name: ''
        };
        console.log('🔴 Setting fallback profile (no email):', fallbackProfile);
        setProfile(fallbackProfile);
        setLoading(false);
        console.log('🔴 Fallback profile set, loading set to false');
        return;
      }

      console.log('🟡 About to insert new profile into database...');
      const insertStart = Date.now();
      
      const { data, error } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          email: email,
          role: 'registrato',
          first_name: '',
          last_name: ''
        })
        .select()
        .single();

      const insertEnd = Date.now();
      console.log(`🟡 Profile insert completed in ${insertEnd - insertStart}ms`);
      console.log('🟡 Insert result - data:', data);
      console.log('🟡 Insert result - error:', error);

      if (error) {
        console.error('🔴 Error creating profile:', error);
        const fallbackProfile = {
          id: userId,
          email: email,
          role: 'registrato',
          first_name: '',
          last_name: ''
        };
        console.log('🔴 Setting fallback profile (insert error):', fallbackProfile);
        setProfile(fallbackProfile);
      } else {
        console.log('🟢 Profile created successfully:', data);
        setProfile(data);
      }
      
    } catch (error) {
      console.error('🔴 CATCH: Error in createDefaultProfile:', error);
      const { data: userData } = await supabase.auth.getUser();
      const finalFallback = {
        id: userId,
        email: userData?.user?.email || '',
        role: 'registrato',
        first_name: '',
        last_name: ''
      };
      console.log('🔴 Setting final fallback profile:', finalFallback);
      setProfile(finalFallback);
    } finally {
      console.log('🟡 FINALLY: Setting loading to false in createDefaultProfile');
      setLoading(false);
      console.log('🟡 FINALLY: Loading set to false in createDefaultProfile');
    }
    
    console.log('🟡 END: createDefaultProfile completed');
  };

  // Sign up new user
  const signUp = async (email, password, additionalData = {}) => {
    try {
      setLoading(true);
      
      console.log('Signing up user with data:', { email, additionalData });
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: additionalData.first_name || '',
            last_name: additionalData.last_name || ''
          }
        }
      });

      if (error) throw error;

      console.log('Signup successful:', data);

      if (data.user && !data.user.email_confirmed_at && additionalData.first_name) {
        console.log('User needs email confirmation, profile will be updated after confirmation');
      } else if (data.user && data.user.email_confirmed_at && additionalData.first_name) {
        console.log('User auto-confirmed, updating profile...');
        await updateProfileAfterSignup(data.user.id, additionalData);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  // Helper function to update profile after signup
  const updateProfileAfterSignup = async (userId, additionalData) => {
    try {
      console.log('Updating profile after signup for user:', userId);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const { data, error } = await supabase
        .rpc('update_user_profile', {
          user_id: userId,
          new_first_name: additionalData.first_name,
          new_last_name: additionalData.last_name
        });

      if (error) {
        console.error('Error updating profile via function:', error);
        const { error: directError } = await supabase
          .from('profiles')
          .update({
            first_name: additionalData.first_name,
            last_name: additionalData.last_name,
          })
          .eq('id', userId);
        
        if (directError) {
          console.error('Error with direct profile update:', directError);
        } else {
          console.log('Profile updated successfully via direct update');
        }
      } else {
        console.log('Profile updated successfully via function:', data);
      }
    } catch (updateErr) {
      console.error('Error in updateProfileAfterSignup:', updateErr);
    }
  };

  // Sign in existing user
  const signIn = async (email, password) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  // Sign out user
  const signOut = async () => {
    try {
      console.log('Signing out user...');
      setLoading(true);
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Supabase signOut error:', error);
      } else {
        console.log('Successfully signed out');
      }
      
      setUser(null);
      setProfile(null);
      setSession(null);

      return { error: null };
    } catch (error) {
      console.error('Sign out error:', error);
      setUser(null);
      setProfile(null);
      setSession(null);
      return { error };
    } finally {
      setLoading(false);
    }
  };

  // Helper functions to check user roles
  const isAdmin = profile?.role === 'admin';
  const isOperator = profile?.role === 'operator';

  const value = {
    user,
    profile,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    isAdmin,
    isOperator,
    isAuthenticated: !!user,
    userRole: profile?.role || null,
    userName: profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.email : null,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};